/**
 * @flow
 * */
import NetworkService from 'core/services/network/NetworkService';
import BaseSDK from 'core/sdk/BaseSDK';
import config from 'config';

/**
 * Risk Weekly configuration API client.
 *
 * A separate SDK from RiskWeeklyReportSDK rather than more methods on it, for the same reason
 * the backend has a separate controller: one serves the numbers, the other serves and edits the
 * configuration behind them. Keeping them apart also keeps the abort-controller names apart, so
 * reloading a configuration list can never cancel a report request that is still wanted.
 *
 * Every endpoint is POST with a body, matching the house style.
 *
 * @class RiskWeeklyConfigSDK
 */
class RiskWeeklyConfigSDK extends BaseSDK {
    _rmtApiService: NetworkService;

    constructor() {
      super();
      this._rmtApiService = new NetworkService(config.rmtApi, []);
    }

    /**
     * getOverview - the small areas whole, plus vocabularies, counts and versions.
     * @returns {Promise<Object>} -
     */
    async getOverview(): Promise<Object> {
      const { body } = await this._rmtApiService.makeAPIPostRequest(
        'RiskWeeklyConfig/GetOverview',
        { body: {}, abortController: this.setAbortController('configOverview') }
      );
      // ResponseModelFilter wraps every action result in ResponseModel { Data }.
      return body.Data;
    }

    /**
     * getPage - one page of a large area: a value map, partners or employees.
     * @param {Object} query - { area, search, isIgnored, skip, take }
     * @returns {Promise<Object>} -
     */
    async getPage(query: Object = {}): Promise<Object> {
      // Named per area: the tabs are independent lists, and a shared controller would let
      // opening Partners cancel a Value Maps page that is still being shown.
      const name = `configPage:${query.area || '-'}`;
      const { body } = await this._rmtApiService.makeAPIPostRequest(
        'RiskWeeklyConfig/GetPage',
        { body: query, abortController: this.setAbortController(name) }
      );
      return body.Data;
    }

    /**
     * getUnmapped - values the last report build could not classify.
     * @returns {Promise<Object>} -
     */
    async getUnmapped(): Promise<Object> {
      const { body } = await this._rmtApiService.makeAPIPostRequest(
        'RiskWeeklyConfig/GetUnmapped',
        { body: {}, abortController: this.setAbortController('configUnmapped') }
      );
      return body.Data;
    }

    /**
     * save - create or update one row, or replace one document.
     * @param {Object} model - area plus the fields that area uses; id 0 inserts
     * @returns {Promise<Object>} -
     */
    async save(model: Object): Promise<Object> {
      // Deliberately NOT abortable. A superseded read is harmless; a cancelled write leaves the
      // operator unsure whether it landed.
      const { body } = await this._rmtApiService.makeAPIPostRequest(
        'RiskWeeklyConfig/Save',
        { body: model }
      );
      return body.Data;
    }

    /**
     * remove - soft-delete one row.
     * @param {Object} query - { area, recordId }
     * @returns {Promise<Object>} -
     */
    async remove(query: Object): Promise<Object> {
      const { body } = await this._rmtApiService.makeAPIPostRequest(
        'RiskWeeklyConfig/Delete',
        { body: query }
      );
      return body.Data;
    }

    /**
     * getHistory - edit history for an area, or for one row within it.
     * @param {Object} query - { area, recordId, take }
     * @returns {Promise<Object>} -
     */
    async getHistory(query: Object = {}): Promise<Object> {
      const name = `configHistory:${query.area || '-'}:${query.recordId || '-'}`;
      const { body } = await this._rmtApiService.makeAPIPostRequest(
        'RiskWeeklyConfig/GetHistory',
        { body: query, abortController: this.setAbortController(name) }
      );
      return body.Data;
    }

    /**
     * cancelCalls
     * @param {Object} params - { name }
     * @returns {void} -
     */
    async cancelCalls(params: { name: string | Array<string> }): Promise<void> {
      this.abortCall(params.name);
    }
}

export default new RiskWeeklyConfigSDK();
